import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { COLORS } from '../../constants/theme';
import SurveyUpload from './SurveyUpload';

const Form = styled.form`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${COLORS.cyan};
  }
  input[type="text"], input[type="number"] {
    width: 100%;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid #333;
    border-radius: 4px;
    color: white;
    font-size: 1rem;
    &:focus {
      outline: none;
      border-color: ${COLORS.cyan};
    }
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const RadioLabel = styled.label`
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: ${({ $active }) => $active ? COLORS.cyan : '#aaa'} !important;
  transition: color 0.2s;
  input[type="radio"] {
    accent-color: ${COLORS.cyan};
    width: auto;
  }
`;

const Note = styled.small`
  display: block;
  margin-top: 0.4rem;
  color: #888;
  font-size: 0.8rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${COLORS.gradientButton};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function PersonaForm({ account, numPersonas, platform, surveyFile, loading, onAccountChange, onNumPersonasChange, onPlatformChange, onSurveyFileChange, onSubmit }) {
  const { t } = useTranslation();
  const isSurvey = platform === 'survey';
  const placeholderKey = platform === 'instagram' ? 'account_placeholder_instagram' : 'account_placeholder_twitter';

  return (
    <Form onSubmit={onSubmit}>
      <FormGroup>
        <label>{t('platform_label')}</label>
        <RadioGroup>
          <RadioLabel $active={platform === 'twitter'}>
            <input
              id="persona-form-platform-twitter-034"
              type="radio"
              name="platform"
              value="twitter"
              checked={platform === 'twitter'}
              onChange={(e) => onPlatformChange(e.target.value)}
            />
            {t('platform_twitter')}
          </RadioLabel>
          <RadioLabel $active={platform === 'instagram'}>
            <input
              id="persona-form-platform-instagram-035"
              type="radio"
              name="platform"
              value="instagram"
              checked={platform === 'instagram'}
              onChange={(e) => onPlatformChange(e.target.value)}
            />
            {t('platform_instagram')}
          </RadioLabel>
          <RadioLabel $active={platform === 'survey'}>
            <input
              id="persona-form-platform-survey-039"
              type="radio"
              name="platform"
              value="survey"
              checked={platform === 'survey'}
              onChange={(e) => onPlatformChange(e.target.value)}
            />
            {t('platform_survey')}
          </RadioLabel>
        </RadioGroup>
      </FormGroup>

      {isSurvey ? (
        <>
          <FormGroup>
            <label>{t('survey_upload_label')}</label>
            <SurveyUpload file={surveyFile} onFileChange={onSurveyFileChange} />
            <Note>{t('survey_note')}</Note>
          </FormGroup>
          <FormGroup>
            <label>{t('survey_name_label')}</label>
            <input
              id="persona-form-account-025"
              type="text"
              value={account}
              onChange={(e) => onAccountChange(e.target.value)}
              placeholder={t('survey_name_placeholder')}
            />
          </FormGroup>
        </>
      ) : (
        <FormGroup>
          <label htmlFor="account">{t('account_label')}</label>
          <input
            id="persona-form-account-025"
            type="text"
            value={account}
            onChange={(e) => onAccountChange(e.target.value)}
            placeholder={t(placeholderKey)}
            required
          />
          {platform === 'instagram' && <Note>{t('instagram_note')}</Note>}
        </FormGroup>
      )}

      <FormGroup>
        <label htmlFor="numPersonas">{t('num_personas_label')}</label>
        <input
          id="persona-form-numpersonas-026"
          type="number"
          value={numPersonas}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            if (!isNaN(value) && value >= 1 && value <= 10) {
              onNumPersonasChange(value);
            }
          }}
          min="1"
          max="10"
          placeholder={t('num_personas_placeholder')}
          required
        />
      </FormGroup>
      <Button id="persona-form-submit-027" type="submit" disabled={loading}>
        {loading ? t('generating') : t('generate_button')}
      </Button>
    </Form>
  );
}

export default PersonaForm;
